import { variantLabel } from '@pos/core';
import type { LocalDb } from '@pos/local-db';

/** Un cambio como lo guarda la cola o la cuarentena. */
export interface ChangeLike {
  table: string;
  kind: 'insert' | 'patch';
  row_id: string;
  payload: Record<string, unknown>;
}

const TABLE_LABEL: Record<string, string> = {
  businesses: 'los datos del negocio',
  memberships: 'un empleado',
  devices: 'un dispositivo',
  categories: 'una categoría',
  suppliers: 'un proveedor',
  products: 'un producto',
  product_variants: 'un precio o variante',
  variant_costs: 'un costo',
  stock_movements: 'un movimiento de stock',
  purchases: 'una compra',
  purchase_items: 'un producto de una compra',
  customers: 'un cliente',
  cash_sessions: 'una caja',
  cash_movements: 'un movimiento de caja',
  account_entries: 'un movimiento de cuenta corriente',
  sync_rejections: 'un cambio rechazado',
};

/** "Venta 0001-00000012 por $ 3.500", "Cambio en un producto “Coca 500”". */
export function describeChange(change: ChangeLike, money: (c: number) => string): string {
  const p = change.payload;
  if (change.table === 'sales') return `${p.kind === 'return' ? 'Devolución' : 'Venta'} ${String(p.number ?? '')} por ${money(Number(p.total ?? 0))}`;
  if (change.table === 'cash_sessions' && change.kind === 'patch' && 'closed_at' in p) return 'Cierre de caja';
  if (change.table === 'cash_sessions' && change.kind === 'insert') return 'Apertura de caja';
  if (change.table === 'sync_rejections' && p.status === 'discarded') return 'Descarte de un cambio rechazado';
  const what = TABLE_LABEL[change.table] ?? change.table;
  const name = typeof p.name === 'string' ? ` “${p.name}”` : '';
  const price = change.table === 'product_variants' && typeof p.price === 'number' ? ` (precio ${money(p.price)})` : '';
  return `${change.kind === 'insert' ? 'Alta de' : 'Cambio en'} ${what}${name}${price}`;
}

const FIELD_LABEL: Record<string, string> = {
  price: 'Precio',
  cost: 'Costo',
  name: 'Nombre',
  sku: 'Código interno',
  barcode: 'Código de barras',
  min_stock: 'Stock mínimo',
  active: 'Activo',
  deleted_at: 'Borrado',
  category_id: 'Categoría',
  supplier_id: 'Proveedor',
  description: 'Descripción',
  tax_bps: 'IVA',
  unit_type: 'Unidad',
  settings: 'Configuración',
  permissions: 'Permisos',
  display_name: 'Nombre',
  phone: 'Teléfono',
  email: 'Email',
  address: 'Dirección',
  credit_limit: 'Límite de crédito',
  notes: 'Notas',
  doc_number: 'Documento',
  tax_condition: 'Condición frente al IVA',
  attributes: 'Variante',
};

export function fieldLabel(field: string): string {
  return FIELD_LABEL[field] ?? field;
}

const MONEY_FIELDS = new Set(['price', 'cost', 'credit_limit']);

/** Valor de un campo para mostrar ("$ 1.500", "Sí", "—"). */
export function formatFieldValue(field: string, value: unknown, money: (c: number) => string): string {
  if (value === null || value === undefined || value === '') return '—';
  if (MONEY_FIELDS.has(field) && typeof value === 'number') return money(value);
  if (field === 'tax_bps' && typeof value === 'number') return `${value / 100} %`;
  if (field === 'min_stock' && typeof value === 'number') return String(value / 1000);
  if (field === 'deleted_at') return 'Sí';
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  if (typeof value === 'object') return 'otra configuración';
  return String(value);
}

/** Nombre del registro afectado, con lo que hay en este dispositivo. */
export async function recordName(db: LocalDb, table: string, id: string): Promise<string> {
  switch (table) {
    case 'products':
      return (await db.products.get(id))?.name ?? 'Producto';
    case 'product_variants':
    case 'variant_costs': {
      const v = await db.product_variants.get(id);
      const p = v ? await db.products.get(v.product_id) : undefined;
      if (!p) return 'Producto';
      const label = v ? variantLabel(v.attributes) : '';
      return label ? `${p.name} (${label})` : p.name;
    }
    case 'categories':
      return (await db.categories.get(id))?.name ?? 'Categoría';
    case 'suppliers':
      return (await db.suppliers.get(id))?.name ?? 'Proveedor';
    case 'customers':
      return (await db.customers.get(id))?.name ?? 'Cliente';
    case 'memberships':
      return (await db.memberships.get(id))?.display_name ?? 'Empleado';
    case 'devices':
      return (await db.devices.get(id))?.name ?? 'Dispositivo';
    case 'businesses':
      return (await db.businesses.get(id))?.name ?? 'Negocio';
    default:
      return TABLE_LABEL[table] ?? table;
  }
}
